import { type ComponentType, type SVGAttributes, useId, useRef, useState } from 'react'

import { useClickOutside } from '../../hooks/useClickOutside'
import { IconBolt, IconCheck, IconChevronDown, IconHome, IconSchool, IconTools } from '../../icons'
import { cn } from '../../utils/cn'
import { AppIcon } from '../AppIcon'
import { Logo } from '../Logo'

export type SwitcherApp = 'academy' | 'prompts' | 'hub' | 'forge'

type TablerIcon = ComponentType<
  SVGAttributes<SVGSVGElement> & { size?: number | string; stroke?: number | string }
>

interface AppDef {
  key: SwitcherApp
  /** Nom affiché après « NeuroForge » */
  label: string
  /** Sous-titre court dans le dropdown */
  tagline: string
  icon: TablerIcon
  /** URL résolue à la volée (env → fallback prod) */
  url: string
}

/**
 * URL d'une app : priorité aux vars Vite (inlinées par app au build),
 * fallback sur le domaine de prod documenté (cf. CLAUDE.md).
 */
function resolveUrl(envUrl: string | undefined, fallback: string): string {
  return envUrl && envUrl.length > 0 ? envUrl : fallback
}

function getApps(): AppDef[] {
  const env = import.meta.env
  return [
    {
      key: 'academy',
      label: 'Academy',
      tagline: 'Apprendre l’IA pas à pas',
      icon: IconSchool,
      url: resolveUrl(env.VITE_ACADEMY_URL, 'https://academy.neuroforge.link'),
    },
    {
      key: 'prompts',
      label: 'Prompts',
      tagline: 'Bibliothèque communautaire',
      icon: IconBolt,
      url: resolveUrl(env.VITE_PROMPTS_URL, 'https://prompts.neuroforge.link'),
    },
    {
      key: 'hub',
      label: 'Hub',
      tagline: 'Le point d’entrée NeuroForge',
      icon: IconHome,
      url: resolveUrl(env.VITE_HUB_URL, 'https://hub.neuroforge.link'),
    },
    {
      key: 'forge',
      label: 'Forge',
      tagline: 'Outils & expérimentations',
      icon: IconTools,
      url: resolveUrl(env.VITE_FORGE_URL, 'https://forge.neuroforge.link'),
    },
  ]
}

export interface AppSwitcherProps {
  /** App courante — marquée active dans le menu */
  current: SwitcherApp
  className?: string
}

/**
 * Sélecteur global d'apps NeuroForge (Academy / Prompts / Hub / Forge).
 * Déclencheur : logo + « NeuroForge <App> » + chevron. Le menu liste les
 * autres sites ; cliquer sur l'app courante renvoie vers son accueil.
 *
 * Styles via tokens partagés (globals.css) → rendu cohérent sur les 4 apps.
 */
export function AppSwitcher({ current, className }: AppSwitcherProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const menuId = useId()
  useClickOutside(ref, () => setOpen(false))

  const apps = getApps()
  const active = apps.find((a) => a.key === current) ?? apps[0]

  return (
    <div
      ref={ref}
      className={cn('relative', className)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false)
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        className={cn(
          'group flex items-center gap-2 rounded-xl px-2 py-1.5 transition-colors',
          'hover:bg-(--bg-elevated)',
        )}
      >
        <Logo size={28} />
        <span className="text-(--text) hidden text-base font-bold sm:inline">
          NeuroForge
          <span className="text-(--accent) ml-1">{active.label}</span>
        </span>
        <AppIcon
          icon={IconChevronDown}
          size={16}
          className={cn('text-(--muted) transition-transform duration-200', open && 'rotate-180')}
          aria-hidden
        />
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Changer d’application NeuroForge"
          className={cn(
            'absolute left-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-2xl p-1.5',
            'border-(--border) border shadow-xl',
            // Fond 100% opaque : un menu est du contenu à lire, pas une surface
            // décorative. `glass` (translucide + backdrop-blur) laissait passer
            // les ambient orbs et le contenu de la page, illisible selon l'app.
            // Les tokens --bg-elevated / --bg-panel sont eux-mêmes en rgba(),
            // d'où des couleurs littérales ici.
            'bg-white dark:bg-slate-900',
          )}
        >
          {apps.map((app) => {
            const isActive = app.key === current
            const Inner = (
              <>
                <span
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
                    isActive ? 'bg-(--accent) text-white' : 'bg-(--bg-elevated) text-(--muted)',
                  )}
                >
                  <AppIcon icon={app.icon} size={20} aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="text-(--text) block text-sm font-semibold">{app.label}</span>
                  <span className="text-(--muted) block truncate text-xs">{app.tagline}</span>
                </span>
                {isActive && (
                  <AppIcon icon={IconCheck} size={16} className="text-(--accent)" aria-hidden />
                )}
              </>
            )

            const itemClass = cn(
              'flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors',
              isActive ? 'bg-(--accent-subtle)' : 'hover:bg-(--bg-elevated)',
            )

            return isActive ? (
              <a key={app.key} href="/" role="menuitem" aria-current="page" className={itemClass}>
                {Inner}
              </a>
            ) : (
              <a key={app.key} href={app.url} role="menuitem" className={itemClass}>
                {Inner}
              </a>
            )
          })}
        </div>
      )}
    </div>
  )
}
